import React, { useState, useEffect, useCallback, useRef  } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast'; 
import axios from 'axios';
import { useTranslation } from 'react-i18next'; // 🎯 الترجمة
import AppSettingsModal from './AppSettingsModal';
import EmailSettings from './EmailSettings';
import AttachmentsModal from './AttachmentsModal';
import AISettingsModal from './AISettingsModal';
import { 
  Save, Search, List, ChevronRight, Filter, Plus, Paperclip, 
  Trash2, ArrowRight, ArrowLeft
} from 'lucide-react';
import UniversalLookup from './UniversalLookup';

import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapPin, Box } from 'lucide-react';
import { EditControl } from 'react-leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';

// 👈 إضافة useMap هنا
import { MapContainer, TileLayer, Marker, Popup, Polygon, FeatureGroup, Tooltip, useMap } from 'react-leaflet';
import BIMViewer from './BIMViewer'; // 👈 استدعاء الـ BIM Viewer
  
// تصليح مشكلة أيقونات Leaflet الافتراضية في React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// 🎯 محرك الكاميرا: بيطير بيك للموقع لما تدوس Drill Down
const MapViewUpdater = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center && center[0] && center[1]) {
      map.flyTo(center, zoom, { animate: true, duration: 1.5 });
    }
  }, [center, zoom, map]);
  return null;
};

const OrganizationDetail = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Organization');
  const [selectedSiteIndex, setSelectedSiteIndex] = useState(null);
  const [isSettingsModalOpen, setIsSettingsModalOpen]= useState(false); 
  const [isEmailSettings, setIsEmailSettings]= useState(false);
  const [isAISettings, setIsAISettings]= useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [fieldMaps, setFieldMaps] = useState({});
  const [count, setCount] = useState(0);
  
  // استيت الخريطة
  const [mapView, setMapView] = useState('MACRO'); 
  const [selectedSiteForMap, setSelectedSiteForMap] = useState(null);
  const [spatialData, setSpatialData] = useState({ locations: [], assets: [] });
  
  // 🎯 استيت الـ 3D Viewer
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewerConfig, setViewerConfig] = useState({ urn: null, dbId: null, recordId: null, bimId: null, description: null, status: null });
  
  const handleOpenViewer = (urn, recordId, bimId, description, status) => {
    setViewerConfig({ urn, recordId, bimId, description, status });
    setIsViewerOpen(true);
  };

  // دالة لجلب الأصول واللوكيشنز الخاصة بـ Site معين
  const fetchSpatialData = async (site_id) => {
    try {
      const res = await axios.get(`/api/spatial/site/${site_id}`);
      setSpatialData(res.data);
      setMapView('MICRO');
    } catch (err) {
      toast.error("Failed to load spatial data for this site");
    }
  };

  const [orgData, setOrgData] = useState({
    orgid: '', description: '', basecurrency1: '', basecurrency2: '',
    itemsetid: '', companysetid: '', status: 'ACTIVE', stockcategory: 'STK',
    clearingaccount: '', active: true, sites: [] 
  });

  const [lookup, setLookup] = useState({ 
    isOpen: false, title: '', type: '', domainId: '', field: '', isSiteLevel: false 
  });

  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

  useEffect(() => {
    const fetchMetadata = async () => {
        try {
            const [orgRes, siteRes] = await Promise.all([
                axios.get('/api/admin/field-map/organizations'),
                axios.get('/api/admin/field-map/sites')
            ]);
            setFieldMaps({ ...orgRes.data, ...siteRes.data });
        } catch (e) {
            console.error("Metadata Load Error");
        }
    };
    fetchMetadata();
  }, []);

  const isNew = !id || id === 'new' || id === 'undefined';
  const loadOrg = async () => {
    if (id && id !== 'new' && id !== 'undefined') {
      axios.get(`/api/organizations/${id}`).then(res => {
        const data = res.data;
        const sanitizedData = {
            ...data,
            orgid: data.org_id || data.orgid,
            sites: data.sites || [],
            ai_settings: data.ai_settings
        };
        setOrgData(sanitizedData);
        if (sanitizedData.sites.length > 0) setSelectedSiteIndex(0);
      }).catch(err => console.error("Error fetching data:", err));
    }
  }
  useEffect(() => {
    loadOrg()
  }, [id]);

  const handleSmartSearch = (fieldName, titleKey, defaultType, isSiteLevel = false) => {
      const maps = fieldMaps || {}; 
      const linkedDomain = maps[fieldName];
      
      setLookup({ 
          isOpen: true, 
          title: t(`org_detail.lookups.${titleKey}`), 
          type: linkedDomain ? 'DOMAIN' : defaultType, 
          domainId: linkedDomain || '', 
          field: fieldName,
          isSiteLevel 
      });
  };

  const fetchCount = useCallback(async () => {
    if (!orgData.orgid || isNew) {
        setCount(0);
        return;
    }
    try {
      const res = await axios.get(`/api/attachments/count/ORGANIZATION/${orgData.orgid}`);
      setCount(res.data.count || 0);
    } catch (err) { 
        console.error("Error fetching attachment count:", err); 
    }
  }, [orgData.orgid, isNew]);
  
  const handleLookupSelect = (row) => {
    const value = row.value || row.id || row.org_id || row.site_id || row.location;
    if (lookup.isSiteLevel && selectedSiteIndex !== null) {
        handleSiteDetailChange(lookup.field, value);
    } else {
        setOrgData(prev => ({ ...prev, [lookup.field]: value }));
    }
    setLookup(prev => ({ ...prev, isOpen: false }));
  };

  const handleOrgChange = (e) => {
    const { name, value, type, checked } = e.target;
    setOrgData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSiteDetailChange = (field, value) => {
    if (selectedSiteIndex === null) return;
    const updatedSites = [...(orgData.sites || [])];
    updatedSites[selectedSiteIndex] = { ...updatedSites[selectedSiteIndex], [field]: value };
    setOrgData({ ...orgData, sites: updatedSites });
  };

  const addNewSiteRow = () => {
    const newSite = { site_id: '', description: '', active: true, state: '', contact: '', year_budget: 0, month_budget: 0 };
    const currentSites = orgData.sites || [];
    const updatedSites = [...currentSites, newSite];
    setOrgData({ ...orgData, sites: updatedSites });
    setSelectedSiteIndex(updatedSites.length - 1); 
  };

  const removeSiteRow = (idx, e) => {
    e.stopPropagation();
    const updatedSites = (orgData.sites || []).filter((_, i) => i !== idx);
    setOrgData({ ...orgData, sites: updatedSites });
    if (selectedSiteIndex === idx) setSelectedSiteIndex(null);
  };

  const handleSave = useCallback(async () => {
    const currentOrgId = orgData.orgid || orgData.org_id;
    if (!currentOrgId) return toast.error(t('org_detail.toasts.id_required'));

    const loadingToast = toast.loading(t('org_detail.toasts.saving'));
    try {
      const payload = {
          ...orgData,
          orgid: currentOrgId,
          sites: orgData.sites || []
      };
      await axios.post('/api/organizations/save', payload);
      toast.dismiss(loadingToast);
      toast.success(t('org_detail.toasts.success'));
      
      if (id === 'new') navigate(`/organizations/${currentOrgId}`);
    } catch (err) {
      toast.dismiss(loadingToast);
      const errorDetail = err.response?.data?.detail || t('org_detail.toasts.fail');
      toast.error(errorDetail, { duration: 5000 });
    }
  }, [orgData, id, navigate, t]);
  
  const handleMapCreate = (e) => {
    if (!selectedSiteForMap) {
      toast.error(t('org.messages.selectSiteToDraw', 'Please drill down into a Site before drawing.'));
      e.layer.remove();
      return;
    }

    const layer = e.layer;
    const geoJsonData = layer.toGeoJSON();
    const coords = geoJsonData.geometry.coordinates[0].map(coord => [coord[1], coord[0]]);

    const centerLat = coords[0][0]; 
    const centerLng = coords[0][1];

    const updatedSites = [...orgData.sites];
    const siteIndex = updatedSites.findIndex(s => s.site_id === selectedSiteForMap.site_id);
    
    if (siteIndex !== -1) {
      updatedSites[siteIndex].geojson_boundary = JSON.stringify(coords);
      updatedSites[siteIndex].lat = centerLat;
      updatedSites[siteIndex].lng = centerLng;
      
      setOrgData({ ...orgData, sites: updatedSites });
      toast.success(`Boundary drawn and saved for site ${selectedSiteForMap.site_id}`);
    }
  };

  const handleMapEdit = (e) => {
  };
  
  const handleSaveAiSettings = async (settingsData) => {
    const currentOrgId = orgData.orgid || orgData.org_id;
    if (!currentOrgId) return toast.error(t('org_detail.toasts.id_required'));

    try {
      const payload = {
        ...settingsData,
        confidence_threshold: Number(settingsData.confidence_threshold),
        max_tasks_count: Number(settingsData.max_tasks_count)
      };

      const response = await axios.post(`/api/organizations/${currentOrgId}/ai-settings`, payload);

      if (response.data.status === 'success') {
        setOrgData(prev => ({
          ...prev,
          ai_settings: settingsData
        }));

        toast.success(t('toasts.save_success'));
        setIsAISettings(false);
      }
    } catch (err) {
      console.error("Error saving AI settings:", err);
      const msg = err.response?.data?.detail || t('toasts.load_fail');
      toast.error(msg);
    }
  };

  useEffect(() => {
    const triggerSave = () => handleSave();
    window.addEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
    return () => window.removeEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
  }, [handleSave]);

  useEffect(() => {
    const AddModalOpen = () => setIsSettingsModalOpen(true);
    window.addEventListener('OPEN_ORG_SETTINGS', AddModalOpen);
    return () => window.removeEventListener('OPEN_ORG_SETTINGS', AddModalOpen);
  }, []);

  useEffect(() => {
    const AddEmailModalOpen = () => setIsEmailSettings(true);
    window.addEventListener('OPEN_EMAIL_SETTINGS', AddEmailModalOpen);
    return () => window.removeEventListener('OPEN_EMAIL_SETTINGS', AddEmailModalOpen);
  }, []);
  
  useEffect(() => {
    const AddAIModalOpen = () => setIsAISettings(true);
    window.addEventListener('OPEN_AI_SETTINGS', AddAIModalOpen);
    return () => window.removeEventListener('OPEN_AI_SETTINGS', AddAIModalOpen);
  }, []);
  
  useEffect(() => {
    fetchCount();
  }, [orgData.orgid, fetchCount]);
  
  // 🎯 Convert the statuses map to an array of objects for the BIMViewer theming
  const currentAssetStatuses = React.useMemo(() => {
    const statusesArray = []; 
    if (spatialData.assets) {
      spatialData.assets.forEach(ast => {
        if (ast.bim_id && ast.status) {
          // Push an object with guid and status, matching what BIMViewer expects
          statusesArray.push({ guid: ast.bim_id, status: ast.status });
        }
      });
    }
    return statusesArray;
  }, [spatialData]);
  
  return (
    <div className={`flex flex-col h-full bg-[#f3f4f6] ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'} text-[11px]`}>
      <UniversalLookup 
        {...lookup}
        onClose={() => setLookup({...lookup, isOpen: false})} 
        onSelect={handleLookupSelect} 
      />
      <AttachmentsModal 
        isOpen={isDocModalOpen} 
        onClose={() => { setIsDocModalOpen(false); fetchCount(); }} 
        ownertable="ORGANIZATION" 
        ownerid={orgData.orgid}
      />
      <AppSettingsModal 
        isOpen={isSettingsModalOpen} 
        onClose={() => { setIsSettingsModalOpen(false); }} 
        orgId={orgData.orgid}
      />     
      <EmailSettings 
        isOpen={isEmailSettings} 
        onClose={() => { setIsEmailSettings(false); }} 
        orgId={orgData.orgid}
      />  
      <AISettingsModal 
        key={orgData.orgid + (orgData.ai_settings ? 'set' : 'unset')}
        isOpen={isAISettings}
        orgId={orgData.orgid}
        ai_provider={orgData?.ai_provider}
        api_key={orgData?.api_key}
        currentSettings={orgData.ai_settings}
        onClose={() => setIsAISettings(false)}
        onSave={handleSaveAiSettings}
      />  

      {/* ================= BIM VIEWER MODAL ================= */}
      {isViewerOpen && (
        <div className="fixed inset-0 z-[9999] bg-black/90 flex flex-col animate-in fade-in duration-300">
          <div className="bg-[#0f172a] text-white p-3 flex justify-between items-center shadow-lg border-b border-slate-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-600 rounded-sm"><Box size={18} /></div>
              <div>
                <h3 className="font-black tracking-widest uppercase text-sm">{t('loc.viewer.title', 'Digital Twin Viewer')}</h3>
                <p className="text-[10px] text-slate-400 font-bold uppercase">{viewerConfig.recordId}</p>
              </div>
            </div>
            <button 
              onClick={() => setIsViewerOpen(false)}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-1.5 rounded-sm font-black uppercase text-[10px] transition-colors"
            >
              {t('common.close', 'Close')}
            </button>
          </div>
          <div className="flex-1 relative">
             <BIMViewer 
                urn={viewerConfig.urn} 
                isActiveTab={isViewerOpen}
                assetStatuses={currentAssetStatuses} 
                activeGuidObj={viewerConfig.bimId ? { 
                    guid: viewerConfig.bimId, 
                    assetnum: viewerConfig.recordId,
                    description: viewerConfig.description,
                    status: viewerConfig.status 
                } : null} 
             />
          </div>
        </div>
      )}

      {/* Header Info */}
      <div className={`bg-white p-2 border-b border-gray-300 flex items-center gap-10 shadow-sm `}>
        <div className={`flex items-center gap-2 ${isRTL ? 'mr-4' : 'ml-4'}`}>
            <label className="text-gray-500 font-bold uppercase tracking-tighter">{t('org_detail.org_label')}</label>
            <input name="orgid" className="border-b border-dashed border-gray-400 w-24 outline-none font-bold text-blue-900 bg-transparent uppercase" 
                   value={orgData.orgid || ''} onChange={handleOrgChange} disabled={!isNew} />
            <div className="relative flex items-center">
               <input name="description" className={`border-b border-gray-400 w-[550px] outline-none text-blue-800 font-bold bg-transparent ${isRTL ? 'pl-6 text-right' : 'pr-6'}`} 
                      value={orgData.description || ''} onChange={handleOrgChange} />
               <List size={14} className={`text-blue-700 cursor-pointer absolute ${isRTL ? 'left-0' : 'right-0'}`} />
            </div>
            {!isNew && <button 
              onClick={() => setIsDocModalOpen(true)}
              className="relative p-2 bg-blue-50 text-blue-900 rounded-sm hover:bg-blue-600 hover:text-white transition-all shadow-sm group"
            >
              <Paperclip size={18} />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-black border border-white">
                  {count}
                </span>
              )}
            </button>}            
        </div>
      </div>

      {/* Tabs */}
      <div className={`flex bg-[#3c70a4] text-white shadow-md`}>
        {['Organization', 'Sites', 'Map'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-8 py-2 font-bold border-r border-white/10 transition-all
            ${activeTab === tab ? 'bg-white text-[#1a3353]' : 'hover:bg-white/10'}`}>
            {t(`org_detail.tabs.${tab.toLowerCase()}`)}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4 custom-scrollbar text-[11px]">
        {activeTab === 'Organization' && (
          <div className="bg-white p-12 border border-gray-300 shadow-sm max-w-5xl mx-auto space-y-6 animate-in fade-in duration-500 " dir={isRTL ? 'rtl' : 'ltr'}>
             <LookupField label={t('org_detail.fields.currency1')} name="basecurrency1" value={orgData.basecurrency1} desc={t('org_detail.descs.currency1')} 
                                isRTL={isRTL} onChange={handleOrgChange} onSearch={() => handleSmartSearch('basecurrency1', 'currency', 'DOMAIN')} />
             
             <LookupField label={t('org_detail.fields.currency2')} name="basecurrency2" value={orgData.basecurrency2} desc={t('org_detail.descs.currency2')} 
                                isRTL={isRTL} onChange={handleOrgChange} onSearch={() => handleSmartSearch('basecurrency2', 'currency', 'DOMAIN')} />
             
             <div className="h-4 border-b border-gray-100" />
             
             <LookupField label={t('org_detail.fields.item_set')} name="itemsetid" value={orgData.itemsetid} desc={t('org_detail.descs.item_set')} 
                                isRTL={isRTL} onChange={handleOrgChange} onSearch={() => handleSmartSearch('itemsetid', 'item_set', 'itemsets')} />
             
             <LookupField label={t('org_detail.fields.company_set')} name="companysetid" value={orgData.companysetid} desc={t('org_detail.descs.company_set')} 
                                isRTL={isRTL} onChange={handleOrgChange} onSearch={() => handleSmartSearch('companysetid', 'company_set', 'companysets')} />
             
             <SearchField label={t('org_detail.fields.item_status')} name="status" value={orgData.status} isRequired 
                                isRTL={isRTL} onChange={handleOrgChange} onSearch={() => handleSmartSearch('status', 'status', 'DOMAIN')} />
             
             <div className={`flex items-center gap-4 mt-8 `}>
                <label className="w-48 text-right font-bold text-gray-500 uppercase tracking-tighter">{t('org_detail.fields.active')}</label>
                <input type="checkbox" name="active" checked={orgData.active || false} onChange={handleOrgChange} className="w-4 h-4" />
             </div>
          </div>
        )}

        {activeTab === 'Sites' && (
          <div className="flex flex-col gap-4 animate-in slide-in-from-right-5 duration-300">
            <div className="bg-white border border-gray-300 shadow-sm overflow-hidden">
              <div className={`p-2 bg-gray-100 border-b flex justify-between items-center text-gray-600 font-bold `}>
                <div className={`flex items-center gap-6 `}>
                  <span className="text-[11px] text-gray-800 uppercase tracking-widest">{t('org_detail.sites.management')}</span>
                  <button onClick={addNewSiteRow} className="flex items-center gap-1 border border-blue-400 px-3 py-0.5 bg-white text-blue-700 font-black hover:bg-blue-600 hover:text-white transition-all shadow-sm">
                    <Plus size={12}/> {t('org_detail.sites.new_row')}
                  </button>
                </div>
                <div className={`text-blue-800 flex items-center gap-2 `}>
                   <ChevronRight size={16} className={`${isRTL ? '' : 'rotate-180'} cursor-pointer`} onClick={() => setSelectedSiteIndex(prev => Math.max(0, prev - 1))} /> 
                   <span className="tracking-widest">{orgData.sites?.length > 0 ? `${selectedSiteIndex + 1} ${t('org_detail.sites.of')} ${orgData.sites.length}` : `0 ${t('org_detail.sites.of')} 0`}</span> 
                   <ChevronRight size={16} className={`${isRTL ? 'rotate-180' : ''} cursor-pointer`} onClick={() => setSelectedSiteIndex(prev => Math.min(orgData.sites.length - 1, prev + 1))} />
                </div>
              </div>
              <table className="w-full border-collapse text-[11px]">
                <thead className="bg-[#f8f9fa] border-b text-gray-500 font-black uppercase tracking-tighter">
                  <tr className={``}>
                    <th className="p-2 border-r w-8"></th>
                    <th className={`p-2 border-r w-48 `}>{t('org_detail.sites.site_id')}</th>
                    <th className={`p-2 border-r `}>{t('org_detail.sites.description')}</th>
                    <th className="p-2 border-r text-center w-20">{t('org_detail.sites.active')}</th>
                    <th className="p-2 w-10 text-center">{t('org_detail.sites.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {orgData.sites?.map((site, idx) => (
                    <tr key={idx} onClick={() => setSelectedSiteIndex(idx)}
                        className={`border-b transition-colors cursor-pointer  ${selectedSiteIndex === idx ? 'bg-[#e0efff]' : 'hover:bg-gray-50'}`}>
                      <td className="p-2 border-r text-center"><ChevronRight size={14} className={`${selectedSiteIndex === idx ? 'text-blue-600' : 'text-gray-300'} ${isRTL ? 'rotate-180' : ''}`}/></td>
                      <td className="p-2 border-r">
                         <input className={`bg-transparent outline-none w-full font-black uppercase text-blue-900 `} 
                                value={site.site_id || site.siteid || ''} 
                                onChange={(e) => handleSiteDetailChange('site_id', e.target.value.toUpperCase())} />
                      </td>
                      <td className="p-2 border-r">
                         <input className={`bg-transparent outline-none w-full font-bold `} value={site.description || ''} 
                                onChange={(e) => handleSiteDetailChange('description', e.target.value)} />
                      </td>
                      <td className="p-2 border-r text-center">
                        <input type="checkbox" checked={site.active} onChange={(e) => handleSiteDetailChange('active', e.target.checked)} />
                      </td>
                      <td className="p-2 text-center" onClick={(e) => removeSiteRow(idx, e)}>
                        <Trash2 size={12} className="text-red-400 hover:text-red-700 mx-auto transition-colors"/>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {selectedSiteIndex !== null && orgData.sites[selectedSiteIndex] && (
              <div className="space-y-4 animate-in slide-up duration-300">
                <div className="bg-white border border-gray-300 shadow-md">
                  <div className={`p-1.5 bg-[#4a5568] text-white font-black uppercase tracking-widest text-[9px] `}>
                    {t('org_detail.sites.site_details')}: {orgData.sites[selectedSiteIndex].site_id}
                  </div>
                  <div className={`p-6 grid grid-cols-2 gap-x-20 gap-y-6 `}>
                     <DetailField label={t('org_detail.sites.state')} value={orgData.sites[selectedSiteIndex].state} hasSearch isRTL={isRTL}
                                  onSearch={() => handleSmartSearch('state', 'governorate', 'DOMAIN', true)} />
                     
                     <DetailField label={t('org_detail.sites.contact')} value={orgData.sites[selectedSiteIndex].contact} hasList isRTL={isRTL}
                                  onChange={(v) => handleSiteDetailChange('contact', v)} />
                  </div>
                </div>

                <div className="bg-white border border-gray-300 shadow-sm">
                  <div className={`p-1.5 bg-[#f3f3f3] border-b font-bold text-gray-600 uppercase tracking-tighter text-[9px] `}>
                    {t('org_detail.sites.fin_info')}
                  </div>
                  <div className={`p-6 grid grid-cols-2 gap-20 `}>
                     <div className="space-y-4">
                        <div className="border-b border-gray-200">
                           <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{t('org_detail.sites.yearly_budget')}</label>
                           <input type="number" className={`w-full bg-transparent outline-none font-bold text-blue-900 pb-1 `} 
                                 value={orgData.sites[selectedSiteIndex].year_budget || 0}
                                 onChange={(e) => handleSiteDetailChange('year_budget', e.target.value)} />
                        </div>
                        <div className="border-b border-gray-200">
                           <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{t('org_detail.sites.monthly_budget')}</label>
                           <input type="number" className={`w-full bg-transparent outline-none font-bold text-blue-900 pb-1 `} 
                                 value={orgData.sites[selectedSiteIndex].month_budget || 0}
                                 onChange={(e) => handleSiteDetailChange('month_budget', e.target.value)} />
                        </div>
                     </div>
                     <div className={`flex flex-col justify-center bg-green-50/50 p-4 border-green-500 rounded-sm ${isRTL ? 'border-r-4 text-right' : 'border-l-4 text-left'}`}>
                        <label className="text-[9px] font-bold text-green-700 uppercase">{t('org_detail.sites.utilization')}</label>
                        <span className="text-lg font-black text-green-900">100.00 %</span>
                        <p className="text-[9px] text-green-600 italic">{t('org_detail.sites.fin_health')}</p>
                     </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {activeTab === 'Map' && (
        <div className="max-w-7xl mx-auto animate-in fade-in duration-500 bg-white p-4 rounded-sm shadow-sm border border-gray-200 flex flex-col h-[700px]">
          
          {/* Header Controls */}
          <div className="flex items-center justify-between mb-4 text-blue-900 border-b pb-2">
            <div className="flex items-center gap-2">
              <MapPin size={20} />
              <h3 className="font-black uppercase tracking-widest">
                {mapView === 'MACRO' ? t('org.mapView', 'Organization Sites Map') : `Site Details: ${selectedSiteForMap?.site_id}`}
              </h3>
            </div>
            
            <div className="flex items-center gap-4">
              {/* 🎯 قائمة اختيار السايت للرسم */}
              <div className="flex items-center gap-2 bg-blue-50 px-3 py-1 rounded-sm border border-blue-100">
                <label className="text-[10px] font-black uppercase text-blue-800 tracking-tighter">
                  Target Site for Drawing:
                </label>
                <select 
                  className="bg-white border border-blue-200 text-blue-900 text-[10px] font-bold py-1 px-2 rounded outline-none cursor-pointer min-w-[150px]"
                  value={selectedSiteForMap?.site_id || ''}
                  onChange={(e) => {
                    const site = orgData.sites.find(s => s.site_id === e.target.value);
                    setSelectedSiteForMap(site);
                    setMapView('MACRO'); 
                  }}
                >
                  <option value="">-- SELECT A SITE --</option>
                  {orgData.sites?.map((s, idx) => (
                    <option key={idx} value={s.site_id}>{s.site_id} - {s.description}</option>
                  ))}
                </select>
              </div>
      
              {mapView === 'MICRO' && (
                <button 
                  onClick={() => {
                    setMapView('MACRO');
                    setSelectedSiteForMap(null); 
                  }}
                  className="bg-slate-200 hover:bg-slate-300 px-3 py-1 rounded-sm text-[10px] font-black uppercase flex items-center gap-1"
                >
                  <ArrowLeft size={12}/> Back to Macro View
                </button>
              )}
            </div>
          </div>
          
          {/* الخريطة */}
          <div className="flex-1 w-full rounded-md overflow-hidden border border-slate-300 shadow-inner z-0">
            <MapContainer 
              center={mapView === 'MICRO' && selectedSiteForMap?.lat ? [selectedSiteForMap.lat, selectedSiteForMap.lng] : [30.0444, 31.2357]} 
              zoom={mapView === 'MICRO' ? 16 : 6} 
              className="h-full w-full"
            >
              {/* 🎯 محرك الكاميرا التلقائي للذهاب إلى الموقع المختار */}
              <MapViewUpdater 
                 center={mapView === 'MICRO' && selectedSiteForMap?.lat ? [selectedSiteForMap.lat, selectedSiteForMap.lng] : [30.0444, 31.2357]} 
                 zoom={mapView === 'MICRO' ? 16 : 6} 
              />

              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; OpenStreetMap Teknora EAM'
              />
              
              {/* ----------------- أدوات الرسم (Draw Controls) ----------------- */}
              <FeatureGroup>
                <EditControl
                  position="topright"
                  onCreated={handleMapCreate}
                  onEdited={handleMapEdit}
                  onDeleted={() => {  }}
                  draw={{
                    polygon: {
                      shapeOptions: { color: '#1a3353', fillColor: '#3b82f6', fillOpacity: 0.2 },
                      allowIntersection: false,
                      drawError: { color: '#e1e100', message: '<strong>Error:</strong> shape edges cannot cross!' },
                    },
                    rectangle: false,
                    circle: false,
                    circlemarker: false,
                    marker: false, 
                    polyline: false,
                  }}
                />
              </FeatureGroup>
      
              {/* ----------------- MACRO VIEW (رسم الفروع) ----------------- */}
              {mapView === 'MACRO' && orgData.sites?.map((site, index) => {
                if (!site.lat || !site.lng) return null;
                return (
                  <React.Fragment key={index}>
                    {site.geojson_boundary && (
                      <Polygon positions={JSON.parse(site.geojson_boundary)} pathOptions={{ color: '#1a3353', fillColor: '#3b82f6', fillOpacity: 0.1 }} >
                        <Tooltip 
                          permanent 
                          direction="center" 
                          className="custom-polygon-label"
                        >
                          {site.description || site.site_id}
                        </Tooltip>
                      </Polygon>
                    )}
                    <Marker position={[site.lat, site.lng]}>
                      <Popup>
                        <div className="p-1 min-w-[150px]">
                          <h4 className="font-black text-blue-900 uppercase border-b pb-1 mb-2 text-[12px]">{site.description || site.site_id}</h4>
                          <button 
                            onClick={() => {
                              setSelectedSiteForMap(site);
                              fetchSpatialData(site.site_id);
                            }}
                            className="bg-blue-600 w-full hover:bg-blue-700 text-white px-2 py-1.5 rounded-sm text-[10px] font-black uppercase flex items-center justify-center gap-1 mt-2"
                          >
                            <MapPin size={12}/> Drill Down (Explore Site)
                          </button>
                        </div>
                      </Popup>
                    </Marker>
                  </React.Fragment>
                );
              })}
      
              {/* ----------------- MICRO VIEW (رسم الأصول واللوكيشنز جوه الفرع) ----------------- */}
              {mapView === 'MICRO' && (
                <>
                  {/* رسم اللوكيشنز */}
                  {spatialData.locations?.map((loc, idx) => (
                    <Marker key={`loc-${idx}`} position={[loc.lat, loc.lng]}>
                      <Popup>
                        <div className="p-1">
                          <span className="bg-orange-100 text-orange-800 text-[8px] px-1 font-black rounded-sm mb-1 inline-block">LOCATION</span>
                          <h4 className="font-black text-blue-900 uppercase text-[12px]">{loc.location_id}</h4>
                          <p className="text-[10px] text-gray-500 italic mb-2">{loc.description}</p>
                          <button onClick={() => navigate(`/locations/${loc.id}`)} className="text-blue-600 text-[10px] font-bold underline">Go to Location Data</button>
                          {loc.model_urn && loc.bim_id && (
                          <button 
                            onClick={() => handleOpenViewer(loc.model_urn, loc.location_id, loc.bim_id, loc.description)}
                            className="bg-[#1a3353] hover:bg-orange-600 text-white px-2 py-1.5 rounded-sm text-[9px] font-black uppercase mt-2 flex items-center gap-1 justify-center transition-colors shadow-md w-full"
                          >
                            <Box size={12} className="text-orange-400"/> Open 3D Twin
                          </button>
                        )}
                        </div>
                      </Popup>
                    </Marker>
                  ))}
      
                  {/* رسم الأصول */}
                  {spatialData.assets?.map((ast, idx) => (
                    <Marker key={`ast-${idx}`} position={[ast.lat, ast.lng]}>
                      <Popup>
                        <div className="p-1">
                          <span className="bg-blue-100 text-blue-800 text-[8px] px-1 font-black rounded-sm mb-1 inline-block">ASSET</span>
                          <h4 className="font-black text-blue-900 uppercase text-[12px]">{ast.assetnum}</h4>
                          <p className="text-[10px] text-gray-500 italic mb-2">{ast.description}</p>
                          
                          <div className="flex flex-col gap-1">
                            <button onClick={() => navigate(`/assets/${ast.asset_id}`)} className="text-blue-600 text-[10px] font-bold underline text-left">View Asset Details</button>
                            
                            {/* 🎯 زرار فتح الـ Digital Twin */}
                            {ast.model_urn && (
                              <button 
                                onClick={() => handleOpenViewer(ast.model_urn, ast.assetnum, ast.bim_id, ast.description,ast.status)}
                                className="bg-[#1a3353] hover:bg-orange-600 text-white px-2 py-1.5 rounded-sm text-[9px] font-black uppercase mt-1 flex items-center gap-1 justify-center transition-colors shadow-md"
                              >
                                <Box size={12} className="text-orange-400"/> Open 3D Twin
                              </button>
                            )}
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </>
              )}
      
            </MapContainer>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

// --- Reusable Shared Components ---

const LookupField = ({ label, value, name, desc, onSearch, onChange, isRTL }) => (
  <div className={`flex items-center gap-4 group `}>
    <label className={`w-48 font-bold text-gray-500 uppercase tracking-tighter ${isRTL ? 'text-right' : 'text-left'}`}>{label}</label>
    <div className={`flex items-center gap-2 border-b border-gray-300 group-focus-within:border-blue-500 transition-colors w-[450px] `}>
      <input name={name} value={value || ''} onChange={onChange} className={`w-24 outline-none font-bold uppercase bg-transparent text-blue-900 `} />
      <ArrowRight size={14} className={`text-blue-700 cursor-pointer hover:scale-110 ${isRTL ? 'rotate-180' : ''}`} onClick={onSearch} />
      <span className={`flex-1 text-gray-400 italic truncate border-gray-100 uppercase text-[10px] ${isRTL ? 'border-r pr-3 text-right' : 'border-l pl-3 text-left'}`}>{desc}</span>
      <List size={14} className="text-blue-700 cursor-pointer" />
    </div>
  </div>
);

const SearchField = ({ label, value, name, isRequired, onChange, onSearch, isRTL }) => (
  <div className={`flex items-center gap-4 `}>
    <label className={`w-48 font-bold uppercase tracking-tighter ${isRTL ? 'text-left' : 'text-right'} ${isRequired ? 'text-red-700' : 'text-gray-500'}`}>
      {isRequired && !isRTL && <span className="mr-1 font-black text-red-500">+</span>}
      {label}
      {isRequired && isRTL && <span className="ml-1 font-black text-red-500">+</span>}
    </label>
    <div className={`flex items-center gap-2 border-b border-gray-300 w-[450px] `}>
      <input name={name} value={value || ''} onChange={onChange} className={`flex-1 outline-none font-bold bg-transparent text-blue-900 uppercase `} />
      <Search size={14} className="text-blue-700 cursor-pointer hover:text-blue-900 transition-colors" onClick={onSearch} />
    </div>
  </div>
);

const DetailField = ({ label, value, isRequired, hasSearch, hasList, onChange, onSearch, isRTL }) => (
  <div className="flex flex-col border-b border-gray-300 focus-within:border-blue-500 transition-all">
    <label className={`text-[10px] font-black uppercase tracking-tighter ${isRequired ? 'text-red-700' : 'text-gray-400'} `}>
      {isRequired && !isRTL && <span className="mr-1">+</span>}
      {label}
      {isRequired && isRTL && <span className="ml-1">+</span>}
    </label>
    <div className={`flex items-center justify-between py-1 min-h-[24px] `}>
       <input className={`font-bold uppercase outline-none bg-transparent flex-1 text-blue-900 text-xs `} value={value || ''} 
              onChange={(e) => onChange && onChange(e.target.value)} />
       <div className="flex gap-2">
         {hasSearch && <Search size={13} className="text-blue-800 cursor-pointer hover:scale-110" onClick={onSearch}/>}
         {hasList && <List size={13} className="text-blue-800 cursor-pointer hover:scale-110"/>}
       </div>
    </div>
  </div>
);

export default OrganizationDetail;